import React from 'react';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useCustomFieldCategoriesAsParams, {
  type CategoryParams,
} from '@/lib/hooks/useCustomFieldCategoriesAsParams';
import translate from '@/lib/intl/translate';
import {
  ALL_CONSTITUENTS_VALUE,
  useSortedCustomFields,
} from '@/modules/Settings';
import type Constituent from '@/types/api/Constituent';
import renderCustomFieldValue from '@/utils/constituents/renderCustomFieldValue';
import ConstituentSectionWrapper from './ConstituentSectionWrapper';

const renderFieldsSection = (
  {
    customFields: fields,
    label,
    value,
  }: {
    customFields: {
      customFieldId: string;
      fieldName: string;
      fieldType: CustomFieldTypes;
    }[];
    label: string;
    value: string;
  },
  constituent: Constituent,
) => {
  const { customFields = [] } = constituent;
  const intersectionFields = fields.filter(
    (field) =>
      field.fieldType === CustomFieldTypes.CHECKBOX ||
      customFields.some(
        (custom) => custom.customFieldId === field.customFieldId,
      ),
  );
  if (intersectionFields.length === 0) return null;

  return (
    <div
      className="mb-6 grid grid-cols-1 gap-y-6 border-b border-grey-300 pb-6 last:mb-0 last:border-0 last:pb-0"
      key={value}
    >
      {value !== ALL_CONSTITUENTS_VALUE && <h3 className="type-h3">{label}</h3>}
      <dl>
        {intersectionFields.map(
          ({ customFieldId, fieldName, fieldType }, i) => {
            const val = customFields.find(
              (cf) => cf.customFieldId === customFieldId,
            )?.value;
            if (!val && fieldType !== CustomFieldTypes.CHECKBOX) return null;
            return (
              <React.Fragment key={customFieldId}>
                <dt className="mb-2 type-body2">{fieldName}</dt>
                <dd
                  className={classNames({
                    'mb-6': i < intersectionFields.length - 1,
                  })}
                >
                  {renderCustomFieldValue(val, fieldType)}
                </dd>
              </React.Fragment>
            );
          },
        )}
      </dl>
    </div>
  );
};

function ConstituentCustomFields({
  constituent,
}: {
  constituent: Constituent;
}) {
  const { categoryParams, resourceValues } =
    useCustomFieldCategoriesAsParams(constituent);

  const { sortedCustomFields = [] } = useSortedCustomFields(false, {
    resourceValues,
  });

  const constituentCustomFields = React.useMemo(
    () =>
      (constituent.customFields || []).reduce((accm, customField) => {
        accm.add(customField.customFieldId);
        return accm;
      }, new Set<string>()),
    [constituent],
  );

  const sections = React.useMemo(
    () =>
      sortedCustomFields
        .map((fields): JSX.Element | null => {
          if (fields.customFields.length === 0) {
            return null;
          }

          const isAllConstituents = fields.value === ALL_CONSTITUENTS_VALUE;
          const constituentHasCategory =
            categoryParams[camelCase(fields.value) as keyof CategoryParams];

          const constituentHasValue = fields.customFields.find((c) =>
            constituentCustomFields.has(c.customFieldId),
          );

          if (
            !isAllConstituents &&
            !constituentHasCategory &&
            !constituentHasValue
          ) {
            return null;
          }

          return renderFieldsSection(fields, constituent);
        })
        .filter(Boolean),
    [sortedCustomFields, constituent, constituentCustomFields],
  );

  return sections.length ? (
    <ConstituentSectionWrapper
      id="constituent-custom-fields-section"
      title={translate('CUSTOM_FIELDS')}
    >
      {sections}
    </ConstituentSectionWrapper>
  ) : null;
}

export default ConstituentCustomFields;
